import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { blockedReasonCopy, consentNotices, type ConsentTenant } from "../lib/userConnections.js";

// ADR-0134 §5.1: the standalone consent screen. A between-apps moment, not an
// in-app task — no app shell, no global nav (which would invite wandering off
// mid-flow and imply a tenant context the flow hasn't established). Session-
// authenticated: an unauthenticated load 401s and apiFetch bounces to
// /login?redirect=/connect?request=… (Login honors the /connect redirect and
// returns here). Copy carries no "OAuth"/token/RFC/ADR terms (§5.4).

// `ConsentTenant` is imported from lib/userConnections, not redeclared here:
// this file used to carry its own copy of the blocked-reason union, which meant
// the "single copy seam" had two definitions and the compiler could not find
// every consumer of a vocabulary change. Device.tsx already imported the shared
// one; both surfaces now read from it (ADR-0160 §4.1).

type ConsentRequest = {
  client_name: string;
  redirect_host: string;
  created_at: string;
  expires_at: string;
  account_display_name: string;
  tenants: ConsentTenant[];
  /** True for hub admins — the card then offers Platform beside the workspaces. */
  platform_available: boolean;
  /**
   * True when this request arrived through a workspace's own connector URL.
   * The workspace is then already decided: render it as a static line, never a
   * picker, and never offer Platform.
   */
  workspace_locked: boolean;
};

/** The workspace select's sentinel for the Platform option — never a real tenant id. */
const PLATFORM_VALUE = "__platform__";

export function Connect() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request") ?? "";
  const [request, setRequest] = useState<ConsentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!requestId) {
        setLoadError("This connection request is missing or invalid — return to Claude and reconnect.");
        setLoading(false);
        return;
      }
      try {
        // A 401 here auto-redirects to /login?redirect=/connect?request=… .
        const res = await apiFetch<ConsentRequest>(`/api/oauth/requests/${encodeURIComponent(requestId)}`);
        if (cancelled) return;
        setRequest(res);
        const firstAllowed = res.tenants.find((t) => t.connect_allowed);
        if (firstAllowed) setSelectedTenantId(firstAllowed.tenant_id);
        else if (res.platform_available) setSelectedTenantId(PLATFORM_VALUE);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : "This connection request is no longer valid — return to Claude and reconnect.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  async function decide(action: "approve" | "deny") {
    setBusy(true);
    setActionError(null);
    try {
      const res = await apiFetch<{ redirect_url: string }>(`/api/oauth/requests/${encodeURIComponent(requestId)}/decision`, {
        method: "POST",
        body: {
          action,
          ...(action === "approve"
            ? selectedTenantId === PLATFORM_VALUE
              ? { context: "platform" }
              : { tenant_id: selectedTenantId }
            : {}),
        },
      });
      // Top-level navigation back to the connecting app (claude.ai).
      window.location.href = res.redirect_url;
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull">
          <div className="muted">Loading…</div>
        </div>
      </div>
    );
  }

  if (loadError || !request) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>Connection request</div>
          <div className="muted">{loadError ?? "This connection request is no longer valid — return to Claude and reconnect."}</div>
        </div>
      </div>
    );
  }

  const allowed = request.tenants.filter((t) => t.connect_allowed);
  const platformSelected = selectedTenantId === PLATFORM_VALUE;
  const selected = allowed.find((t) => t.tenant_id === selectedTenantId) ?? (platformSelected ? null : allowed[0] ?? null);
  const canApprove = platformSelected || (allowed.length > 0 && Boolean(selected));
  const choiceCount = allowed.length + (request.platform_available ? 1 : 0);

  return (
    <div className="centerPage">
      <div className="card pageNarrow wFull" style={{ display: "grid", gap: 14 }}>
        <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em" }}>Connect to Atlas</div>

        <div className="muted" style={{ fontSize: 14 }}>
          <b style={{ color: "var(--text)" }}>{request.client_name}</b>{" "}
          <span className="muted" style={{ fontSize: 12 }}>(as reported by the connecting app)</span> wants to access Atlas as you.
        </div>

        <div className="field" style={{ marginBottom: 0 }}>
          <span className="label">Returning to</span>
          <div style={{ fontWeight: 600 }}>{request.redirect_host}</div>
        </div>

        {canApprove ? (
          <>
            {!request.workspace_locked && choiceCount > 1 ? (
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="label">Connect to</span>
                <select className="select" value={selectedTenantId} disabled={busy} onChange={(e) => setSelectedTenantId(e.target.value)}>
                  {allowed.map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>
                  ))}
                  {request.platform_available ? (
                    <option value={PLATFORM_VALUE}>Platform — hub administration</option>
                  ) : null}
                </select>
              </label>
            ) : (
              <div className="field" style={{ marginBottom: 0 }}>
                <span className="label">Connect to</span>
                <div style={{ fontWeight: 600 }}>
                  {platformSelected ? "Platform — hub administration" : selected?.tenant_name}
                </div>
              </div>
            )}

            <div className="help">
              You'll connect as <b>{request.account_display_name}</b>
              {platformSelected ? (
                <> with hub administrator access.</>
              ) : selected?.role_name ? (
                <> with the <b>{selected.role_name}</b> role.</>
              ) : (
                "."
              )}
            </div>

            {/* Per-workspace: what is true for the one being connected to, not a
                blanket claim. A workspace that records nothing carries no notice. */}
            {consentNotices(selected).map((notice) => (
              <div key={notice} className="help">{notice}</div>
            ))}

            {actionError ? <div className="help" style={{ color: "var(--danger-text)" }}>{actionError}</div> : null}

            <div className="actions">
              <button className="btn" type="button" disabled={busy} onClick={() => void decide("deny")}>Deny</button>
              <button className="btn btnPrimary" type="button" disabled={busy} onClick={() => void decide("approve")}>Approve</button>
            </div>
          </>
        ) : (
          <>
            <div className="muted">
              {request.tenants.length === 0
                ? request.workspace_locked
                  // Deliberately identical whether the workspace does not exist
                  // or this person simply is not in it — and it names no
                  // workspace, so the URL cannot be probed for who exists.
                  ? "You don't have a connection role in this workspace yet. Ask a workspace admin to assign you one."
                  : "You don't have a connection role in any workspace yet. Ask a workspace admin to assign you one in Users & Teams."
                : blockedReasonCopy(request.tenants[0].blocked_reason)}
            </div>
            {actionError ? <div className="help" style={{ color: "var(--danger-text)" }}>{actionError}</div> : null}
            <div className="actions">
              <button className="btn" type="button" disabled={busy} onClick={() => void decide("deny")}>Deny</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
